/**
 * accountDetailView.ts — the account page's rules as pure functions (Account
 * detail v3 AD1, design note §45), so vitest pins each one without a DOM.
 *
 * Every figure here is read from the server's summary (pkg/contracts
 * SalesAccountSummary) or the accounts list read; nothing is summed in the
 * browser. Day counts are London calendar days (dealHealthCard's helpers).
 */
import type { SalesAccountSummary } from '../../../../api/types'
import { daysBetween, londonDayOf, closeDay } from './dealHealthCard'

/** The accounts list read's page the stepper orders (the server's cap). A
 *  read that comes back this full may hold more accounts than it returned, so
 *  "n of N" is then not stated. */
export const STEP_CAP = 500

export interface StepRow { id: string; name: string; openValueBasePence: number }

/** "by open value": the largest open pipeline first, then the name, then the
 *  id — the same order every visit. */
export function stepOrder(rows: readonly StepRow[]): string[] {
  return [...rows]
    .sort((a, b) => (b.openValueBasePence - a.openValueBasePence)
      || a.name.localeCompare(b.name, 'en-GB', { sensitivity: 'base' })
      || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0))
    .map(r => r.id)
}

export interface StepPosition { index: number; total: number; prev: string | null; next: string | null }

/** Where the account sits in the order; null when it is not in the read (a
 *  new account, a failed read) — the page then shows no counter. */
export function stepPosition(order: readonly string[], id: string): StepPosition | null {
  const index = order.indexOf(id)
  if (index < 0) return null
  return {
    index, total: order.length,
    prev: index > 0 ? order[index - 1] : null,
    next: index < order.length - 1 ? order[index + 1] : null,
  }
}

/** §45: Prospect = no won deal on the account; Customer = at least one. */
export function relationshipChip(s: SalesAccountSummary): 'Prospect' | 'Customer' {
  return s.wonDealCount > 0 ? 'Customer' : 'Prospect'
}

/** §45: "Never contacted" until the first touch (a done call, email or
 *  meeting) exists, then "First call logged" (the spec's words). */
export function contactChip(s: SalesAccountSummary): 'Never contacted' | 'First call logged' {
  return s.touchCount > 0 ? 'First call logged' : 'Never contacted'
}

/** Whole London days since the account was created; null for no date. */
export function accountAgeDays(createdAt: string | null | undefined, today: string): number | null {
  const day = londonDayOf(createdAt)
  return day ? Math.max(0, daysBetween(day, today)) : null
}

const days = (n: number) => (n === 1 ? '1 day' : `${n} days`)

/** "in 32 days" / "today" / "3 days ago" — a close date against today. */
export function closeRelative(expectedClose: string | null | undefined, today: string): string | null {
  const day = closeDay(expectedClose)
  if (!day) return null
  const n = daysBetween(today, day)
  if (n === 0) return 'today'
  return n > 0 ? `in ${days(n)}` : `${days(-n)} ago`
}

/** "account is 47 days old" — the Last touch tile's second line. */
export function ageLine(ageDays: number | null): string | null {
  if (ageDays === null) return null
  return ageDays === 0 ? 'account created today' : `account is ${days(ageDays)} old`
}

/** The empty-activity banner (spec §5): shown while the account has no touch.
 *  The second sentence only when it is true — no activities of any kind and
 *  at least one recorded change. */
export function emptyActivityBanner(s: SalesAccountSummary, ageDays: number | null, changeCount: number): string | null {
  if (s.touchCount > 0) return null
  const first = ageDays === null || ageDays === 0
    ? 'No calls, emails or meetings yet.'
    : `No calls, emails or meetings in ${days(ageDays)}.`
  return s.activityCount === 0 && changeCount > 0 ? `${first} Only field edits so far.` : first
}
